import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import Carousel from "./Carousel";
import { isPresent, hasItems, getYouTubeId } from "./helpers";

function VideoCard({ video }) {
  const ytId = getYouTubeId(video.video_url);
  if (!ytId) return null;

  const isShorts = video.video_url?.includes("shorts");

  return (
    <div className="bg-[var(--dvx-surface)] border border-[var(--dvx-border)] overflow-hidden transition-colors duration-300">
      <div
        className="w-full overflow-hidden"
        style={{ height: isShorts ? "320px" : "200px" }}
      >
        <iframe
          src={`https://www.youtube.com/embed/${ytId}?rel=0&modestbranding=1`}
          title={video.title || "Video"}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          loading="lazy"
          className="w-full h-full border-0"
        />
      </div>

      <div className="p-4">
        {isPresent(video.title) && (
          <h3 className="font-oswald text-[var(--dvx-text)] font-bold uppercase text-sm leading-tight mb-1">
            {video.title}
          </h3>
        )}
        {isPresent(video.description) && (
          <p className="text-[var(--dvx-text-muted)] font-manrope text-xs line-clamp-2">
            {video.description}
          </p>
        )}
      </div>
    </div>
  );
}

/* LEFT FORM */
function LeadForm({ onSubmit }) {
  return (
    <div className="bg-[var(--dvx-surface)] border border-[var(--dvx-border)] p-5 w-full">
      <h3 className="font-oswald text-lg font-bold text-[var(--dvx-text)] uppercase mb-3">
        Get Free Consultation
      </h3>

      <form onSubmit={onSubmit} className="flex flex-col gap-3">
        <input
          type="text"
          placeholder="Your Name"
          className="px-3 py-2 text-sm border border-[var(--dvx-border)] bg-transparent outline-none"
          required
        />

        <input
          type="email"
          placeholder="Your Email"
          className="px-3 py-2 text-sm border border-[var(--dvx-border)] bg-transparent outline-none"
          required
        />

        <button
          type="submit"
          className="bg-[var(--dvx-primary)] text-white py-2 text-sm font-semibold uppercase hover:opacity-90 transition"
        >
          Submit
        </button>
      </form>
    </div>
  );
}

/* RIGHT CTA */
function SideCTA({ cta, book }) {
  return (
    <div className="bg-[var(--dvx-surface)] border border-[var(--dvx-border)] p-5 w-full">
      {book == true && (
        <p className="text-[var(--dvx-text-muted)] text-sm mb-4">
          Start your transformation today. Speak with our experts or book your
          personalized assessment now.
        </p>
      )}
      {book == false && (
        <p className="text-[var(--dvx-text-muted)] text-sm mb-4">
          Start your transformation today. Call our experts or book your
          personalized assessment now.
        </p>
      )}
      <div className="flex flex-col gap-3">
        {isPresent(cta?.call) && book == false && (
          <a
            href={`tel:${cta.call}`}
            className="text-center px-4 py-2 bg-[var(--dvx-primary)] text-white text-sm font-semibold hover:opacity-90 transition"
          >
            Call Now
          </a>
        )}
        {book == true && (
          <a
            href={cta?.bookLink || "/contact"}
            className="text-center px-4 py-2 bg-[var(--dvx-primary)] text-white text-sm font-semibold hover:opacity-90 transition"
          >
            Book Assessment
          </a>
        )}
        {isPresent(cta?.whatsapp) && book == false && (
          <a
            href={`https://wa.me/${cta.whatsapp}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-center px-4 py-2 border border-green-500 text-green-600 text-sm font-semibold hover:bg-green-500 hover:text-white transition"
          >
            WhatsApp
          </a>
        )}
      </div>
    </div>
  );
}

export default function VideoSection({ section, videos }) {
  if (!section) return null;

  const items = (videos || []).filter(
    (v) => isPresent(v.video_url) && v.is_active !== false,
  );
  if (!hasItems(items)) return null;

  const [itemsPerPage, setItemsPerPage] = useState(2);

  useEffect(() => {
    const updateItems = () => {
      if (window.innerWidth < 768) {
        setItemsPerPage(1); // mobile
      } else {
        setItemsPerPage(2); // desktop
      }
    };

    updateItems(); // run once
    window.addEventListener("resize", updateItems);

    return () => window.removeEventListener("resize", updateItems);
  }, []);

  return (
    <section
      data-testid="video-section"
      className="py-24 bg-[var(--dvx-bg)] transition-colors duration-300"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* HEADER */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-10"
        >
          {isPresent(section.badge) && (
            <h3 className="text-[var(--dvx-primary)] font-oswald text-xl font-semibold uppercase tracking-[0.20.em]">
              {section.badge}
            </h3>
          )}
          {isPresent(section.title) && (
            <h2 className="mt-4 font-oswald text-4xl font-bold text-[var(--dvx-text)] uppercase tracking-tight">
              {section.title}
            </h2>
          )}
          {isPresent(section.description) && (
            <p className="mt-3 text-[var(--dvx-text-muted)] font-manrope text-base max-w-xl mx-auto">
              {section.description}
            </p>
          )}
        </motion.div>

        {/* MAIN GRID */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 items-start">
          {/* LEFT FORM */}
          <div className="lg:col-span-1">
            {/* <LeadForm onSubmit={(e) => e.preventDefault()} /> */}
            <SideCTA cta={section} book={true} />
          </div>

          {/* CENTER CAROUSEL */}
          <div className="lg:col-span-2">
            <Carousel
              items={items}
              itemsPerPage={itemsPerPage}
              autoplayMs={5000}
              pauseOnHover={true}
              renderItem={(video) => <VideoCard video={video} />}
            />
          </div>

          {/* RIGHT CTA */}
          <div className="lg:col-span-1">
            <SideCTA cta={section} book={false} />
          </div>
        </div>
      </div>
    </section>
  );
}
